import React, { useState, useEffect } from "react";
import { Upload } from "lucide-react";
import { supabase } from "../../utils/supabase";

interface UploadStickerFormProps {
  onSubmit: (file: File, name: string, category: string) => void;
  onCancel: () => void;
  uploading: boolean;
}

const UploadStickerForm: React.FC<UploadStickerFormProps> = ({
  onSubmit,
  onCancel,
  uploading,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [customCategory, setCustomCategory] = useState("");
  const [useCustomCategory, setUseCustomCategory] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [existingCategories, setExistingCategories] = useState<string[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  useEffect(() => {
    loadExistingCategories();
  }, []);

  const loadExistingCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("stickers")
        .select("category")
        .not("category", "is", null);

      if (error) throw error;

      const uniqueCategories = Array.from(
        new Set(data?.map((item) => item.category) || [])
      ).sort();

      setExistingCategories(uniqueCategories);

      // Set the first category as default if available
      if (uniqueCategories.length > 0) {
        setCategory(uniqueCategories[0]);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setName(selectedFile.name.split(".")[0]); // Use filename as default name

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (file && name.trim()) {
      const finalCategory = useCustomCategory
        ? customCategory.trim()
        : category;
      if (finalCategory) {
        onSubmit(file, name.trim(), finalCategory);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* File Upload */}
      <div>
        <label
          className="block text-sm font-medium text-gray-700 mb-2"
          dir="rtl"
        >
          בחר קובץ תמונה
        </label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
            id="file-upload"
          />
          <label htmlFor="file-upload" className="cursor-pointer">
            {preview ? (
              <div className="space-y-2">
                <img
                  src={preview}
                  alt="Preview"
                  className="max-h-32 mx-auto rounded"
                />
                <p className="text-sm text-gray-600">{file?.name}</p>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="h-8 w-8 text-gray-400 mx-auto" />
                <p className="text-sm text-gray-600" dir="rtl">
                  לחץ לבחירת קובץ
                </p>
              </div>
            )}
          </label>
        </div>
      </div>

      {/* Name Input */}
      <div>
        <label
          className="block text-sm font-medium text-gray-700 mb-2"
          dir="rtl"
        >
          שם הסטיקר
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
          placeholder="הכנס שם לסטיקר"
          dir="rtl"
          required
        />
      </div>

      {/* Category Input */}
      <div>
        <label
          className="block text-sm font-medium text-gray-700 mb-2"
          dir="rtl"
        >
          קטגוריה
        </label>

        {/* Category Type Selection */}
        <div className="mb-3">
          <div className="flex space-x-4" dir="rtl">
            <label className="flex items-center">
              <input
                type="radio"
                name="categoryType"
                checked={!useCustomCategory}
                onChange={() => setUseCustomCategory(false)}
                className="mr-2"
              />
              <span className="text-sm">קטגוריה מוגדרת מראש</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="categoryType"
                checked={useCustomCategory}
                onChange={() => setUseCustomCategory(true)}
                className="mr-2"
              />
              <span className="text-sm">קטגוריה מותאמת אישית</span>
            </label>
          </div>
        </div>

        {/* Category Selection */}
        {!useCustomCategory ? (
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
            dir="rtl"
            disabled={loadingCategories}
          >
            {loadingCategories ? (
              <option value="">טוען קטגוריות...</option>
            ) : existingCategories.length > 0 ? (
              existingCategories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))
            ) : (
              <option value="">אין קטגוריות קיימות</option>
            )}
          </select>
        ) : (
          <input
            type="text"
            value={customCategory}
            onChange={(e) => setCustomCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
            placeholder="הכנס שם קטגוריה חדשה"
            dir="rtl"
            required={useCustomCategory}
          />
        )}
      </div>

      {/* Buttons */}
      <div className="flex space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          dir="rtl"
        >
          ביטול
        </button>
        <button
          type="submit"
          disabled={
            !file ||
            !name.trim() ||
            uploading ||
            (useCustomCategory && !customCategory.trim()) ||
            (!useCustomCategory && !category.trim())
          }
          className="flex-1 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          dir="rtl"
        >
          {uploading ? "מעלה..." : "העלה"}
        </button>
      </div>
    </form>
  );
};

export default UploadStickerForm;
